import { useEffect, useState } from "react";
import { apiFetch } from "../../api/client";

export type BoardPromoPayload = {
  enabled: boolean;
  href: string;
  brand: string;
  title: string;
  subtitle: string;
  cta: string;
};

type Props = {
  /**
   * Реклама школы только для внешней аудитории на бесплатном тарифе.
   * `false` / `null` (ещё не известен тип) — не показывать и не запрашивать.
   */
  visible?: boolean;
};

/** Сколько полоска не показывается после «Потом гляну». */
const SNOOZE_MS = 24 * 60 * 60 * 1000;

const SNOOZE_KEY = "wb-board-promo-snooze-until";

function snoozedNow(): boolean {
  try {
    const raw = window.localStorage.getItem(SNOOZE_KEY);
    if (!raw) return false;
    const until = Number(raw);
    if (!Number.isFinite(until)) return false;
    return Date.now() < until;
  } catch {
    // Приватный режим или запрет на хранилище — просто показываем полоску.
    return false;
  }
}

function snooze(): void {
  try {
    window.localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
  } catch {
    // Не сохранилось — полоска вернётся при следующем открытии доски.
  }
}

/**
 * Рекламная полоска онлайн-школы Bezpart между шапкой доски и «Доступ».
 * Школьным аккаунтам и платным тарифам не показывается, остальные могут
 * убрать её на сутки кнопкой «Потом гляну».
 */
export function BoardSchoolPromoStrip({ visible = true }: Props) {
  const [promo, setPromo] = useState<BoardPromoPayload | null>(null);
  const [hidden, setHidden] = useState(() => snoozedNow());

  useEffect(() => {
    if (!visible || hidden) {
      setPromo(null);
      return;
    }
    let cancelled = false;
    void apiFetch<BoardPromoPayload>("/api/v1/site/board-promo")
      .then((data) => {
        if (cancelled) return;
        setPromo(data?.enabled ? data : null);
      })
      .catch(() => {
        if (!cancelled) setPromo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [visible, hidden]);

  if (!visible || hidden || !promo) return null;

  return (
    <aside className="wb-board-school-promo" aria-label="Реклама онлайн-школы Bezpart">
      <div className="wb-board-school-promo__inner">
        <div className="wb-board-school-promo__copy">
          <span className="wb-board-school-promo__brand">{promo.brand}</span>
          <span className="wb-board-school-promo__title">{promo.title}</span>
          <span className="wb-board-school-promo__sub">{promo.subtitle}</span>
        </div>
        <div className="wb-board-school-promo__actions">
          <a
            className="wb-board-school-promo__cta"
            href={promo.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {promo.cta}
          </a>
          <button
            type="button"
            className="wb-board-school-promo__dismiss"
            title="Скрыть на сутки"
            onClick={() => {
              snooze();
              setHidden(true);
            }}
          >
            Потом гляну 😊
          </button>
        </div>
      </div>
    </aside>
  );
}
